<template>
    <v-form
        ref="form"
        @submit.prevent="onSubmit"
    >
        <v-row>
            <v-col
                v-if="!fromHomepage"
                cols="12"
                class="text-center"
            >
                <h2 class="display-1 mb-2">
                    Lets get you started
                </h2>
                <p class="body-1 font-weight-light">
                    Enter your email and desired password.
                </p>
            </v-col>

            <v-col cols="12">
                <v-text-field
                    v-model="email"
                    type="email"
                    label="Email Address"
                    validate-on-blur
                    :rules="[
                        rules.required,
                        rules.email
                    ]"
                    autocomplete="username email"
                    color="secondary"
                />
            </v-col>

            <v-col cols="12">
                <v-text-field
                    label="Password"
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                    @click:append="showPassword = !showPassword"
                    :rules="[
                        rules.required,
                        rules.nospaces,
                        rules.password.length
                    ]"
                    :hint="passwordHint"
                    color="secondary"
                    autocomplete="new-password"
                />
            </v-col>

            <v-col cols="12">
                <v-text-field
                    label="Confirm Password"
                    v-model="confirmPassword"
                    :type="showPassword ? 'text' : 'password'"
                    :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                    @click:append="showPassword = !showPassword"
                    :rules="[
                        rules.password.confirmPassword
                    ]"
                    color="secondary"
                    autocomplete="new-password"
                />
            </v-col>

            <v-col cols="12">
                <v-checkbox
                    color="secondary"
                    v-model="agreement"
                    :rules="[
                        rules.required
                    ]"
                >
                    <template v-slot:label>
                        <div>
                            I agree to Come Connect's
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <a
                                        :href="tocRoute"
                                        target="_blank"
                                        @click.stop
                                        v-on="on"
                                    >
                                        Terms &amp; Conditions
                                    </a>
                                </template>
                                Opens in new window
                            </v-tooltip>
                            and
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <a
                                        :href="privacyPolicyRoute"
                                        target="_blank"
                                        @click.stop
                                        v-on="on"
                                    >
                                        Privacy Policy
                                    </a>
                                </template>
                                Opens in new window
                            </v-tooltip>
                        </div>
                    </template>
                </v-checkbox>
            </v-col>

            <v-col cols="12">
                <div class="d-flex">
                    <v-spacer />
                    <v-btn
                        type="submit"
                        :loading="isSubmitting"
                        :disabled="isSubmitting"
                        block
                        large
                        color="primary"
                    >
                        Sign Up
                    </v-btn>
                </div>
            </v-col>
        </v-row>
        <span>Already have an account? <router-link :to="{name: 'login'}">Login</router-link></span>
    </v-form>
</template>

<script>
import axios from 'axios'
import UserBus from '../../utils/buses/UserBus'
import {
    requiredValidation,
    emailValidation,
    noSpacesValidation,
    minPasswordLengthValidation
} from '../../utils/validations'
import MessageBus from '../../utils/buses/MessageBus'
import { default422Error, defaultError } from '../../utils/constants/errors'
import { passwordHint } from '../../utils/constants/hints'

export default {
    props: {
        fromHomepage: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            email: null,
            password: null,
            confirmPassword: null,
            showPassword: false,
            isSubmitting: false,
            passwordHint,
            rules: {
                nospaces: noSpacesValidation,
                required: requiredValidation,
                email: emailValidation,
                password: {
                    length: minPasswordLengthValidation,
                    confirmPassword: value => value === this.password || 'Passwords do not match'
                }
            },
            agreement: false,
            tocRoute: this.$router.resolve({ name: 'termsAndConditions' }).href,
            privacyPolicyRoute: this.$router.resolve({ name: 'privacyPolicy' }).href
        }
    },
    methods: {
        async onSubmit () {
            if (!this.$refs.form.validate()) {
                MessageBus.sendMessage(default422Error, 'error')
                return
            }

            this.isSubmitting = true

            try {
                const res = await axios.post('/auth/signup', {
                    email: this.email,
                    password: this.password
                })

                // log in user
                const { token } = res.data

                UserBus.setToken(token)

                this.$router.push({ name: 'verifyEmail' })
            } catch (err) {
                console.error('Error signing up', err)

                // email is in use error
                if (
                    err.status === 409
                    && err.data
                    && err.data.field
                    && err.data.field === 'email'
                ) {
                    MessageBus.sendMessage('Email is already in use', 'error')
                    return
                }

                MessageBus.sendMessage(defaultError, 'error')
                return
            } finally {
                this.isSubmitting = false
            }

            try {
                await axios.post('/auth/verification-email')
            } catch (err) {
                console.error('Error sending verification email', err)
                MessageBus.sendMessage('Error sending verification email. Please resend email.', 'error')
            }
        }
    }
}
</script>
